import styled, { css } from 'styled-components';
import { SurfaceCard } from '@/styles/shared.styled';

export const UploadZone = styled.div<{ $active?: boolean; $rejected?: boolean }>`
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 2px dashed ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.radii['2xl']};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing[10]} ${({ theme }) => theme.spacing[6]};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.base};
  text-align: center;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background-color: rgba(27, 42, 74, 0.02);
  }

  ${({ $active, theme }) =>
    $active &&
    css`
      border-color: ${theme.colors.accent};
      background-color: rgba(212, 168, 67, 0.05);
    `}

  ${({ $rejected, theme }) =>
    $rejected &&
    css`
      border-color: ${theme.colors.danger};
      background-color: rgba(220, 38, 38, 0.02);
    `}
`;

export const UploadZoneIcon = styled.div`
  width: 3.5rem;
  height: 3.5rem;
  color: ${({ theme }) => theme.colors.gray400};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const UploadZoneTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

export const UploadZoneSubtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing[1]};
`;

export const UploadZoneFormats = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const BrowseFolderButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  width: 100%;
  margin-top: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]} ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.bgSurface};
  border: 1px dashed ${({ theme }) => theme.colors.gray300};
  border-radius: ${({ theme }) => theme.radii.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.base};

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

export const UploadProgressCard = styled(SurfaceCard)`
  margin-top: ${({ theme }) => theme.spacing[3]};
`;

export const UploadProgressFile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

export const UploadProgressFilename = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const UploadProgressSize = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textMuted};
`;

export const UploadProgressBar = styled.div`
  height: 0.25rem;
  background-color: ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.radii.full};
  overflow: hidden;
`;

export const UploadProgressBarFill = styled.div<{ $status: 'pending' | 'uploading' | 'done' | 'error' }>`
  height: 100%;
  border-radius: ${({ theme }) => theme.radii.full};
  transition: width ${({ theme }) => theme.transitions.base};
  background-color: ${({ theme, $status }) => {
    if ($status === 'done') return theme.colors.success;
    if ($status === 'error') return theme.colors.danger;
    return theme.colors.primary;
  }};
`;

export const UploadUsageInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const UploadUsageBarTrack = styled.div`
  height: 0.375rem;
  background-color: ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.radii.full};
  overflow: hidden;
  flex: 1;
  margin-left: ${({ theme }) => theme.spacing[3]};
`;

export const UploadUsageBarFill = styled.div<{ $percent: number; $warning?: boolean }>`
  height: 100%;
  border-radius: ${({ theme }) => theme.radii.full};
  transition: width ${({ theme }) => theme.transitions.base};
  width: ${({ $percent }) => Math.min($percent, 100)}%;
  background-color: ${({ theme, $warning }) =>
    $warning ? theme.colors.warning : theme.colors.primary};
`;

export const AssignmentSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  padding-bottom: ${({ theme }) => theme.spacing[4]};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray200};
`;

export const AssignmentRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  margin-bottom: ${({ theme }) => theme.spacing[3]};

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

export const AssignmentField = styled.div`
  flex: 1;

  label {
    display: block;
    font-size: ${({ theme }) => theme.fontSizes.sm};
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-bottom: ${({ theme }) => theme.spacing[1]};
  }
`;

export { HiddenFileInput } from '@/styles/shared.styled';
